<template>
    <div
        v-show="visible"
        ref="wrap"
        class="el-select-dropdown el-popper qa-select-dropdown"
        :class="[
            { 'is-multiple': select?.props?.multiple },
            select?.props?.popperClass,
            { 'dynamic-width': dynamicWidth }
        ]"
        :style="dropdownStyle"
    >
        <div
            v-if="filterable"
            class="el-select-dropdown-search qa-select-dropdown-search"
        >
            <el-input
                ref="search"
                v-model="searchValue"
                class="el-select-dropdown-search-form qa-select-dropdown-search-form"
                :placeholder="searchPlaceholder"
                type="embedded"
                clearable
                @keydown="scrollToReference"
                @input="onSearchEdit"
            >
                <template #suffix>
                    <a-glyph name="search" class="el-input__icon" />
                </template>
            </el-input>
            <el-divider />
        </div>
        <slot />
    </div>
</template>

<script setup>
import { ref, computed, watch, inject, onMounted, onBeforeUnmount, nextTick } from 'vue';
import ElDivider from '@core/components/divider/divider.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ElInput from '@core/components/input/input.vue';

const props = defineProps({
    maxWidth: { type: String, default: null },
    dynamicWidth: { type: Boolean, default: false },
    visible: { type: Boolean, default: false },
    filterable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, required: true },
});

const emit = defineEmits(['searchedit', 'onHidePopper']);

const select = inject('select', null);
const search = ref(null);
const wrap = ref(null);
const referenceElm = ref(null);
const minWidth = ref('');
const windowWidth = ref('');

const searchValue = computed({
    get: () => select?.query?.value ?? '',
    set: val => { if (select?.query) select.query.value = val; },
});

const dropdownStyle = computed(() => {
    const min = minWidth.value;
    return {
        minWidth: min,
        maxWidth: props.dynamicWidth ? (windowWidth.value || min) : (props.maxWidth || min),
    };
});

function onSearchEdit(val) {
    emit('searchedit', val);
}

function scrollToReference() {
    const rect = referenceElm.value?.getBoundingClientRect?.();
    if (!rect) return;

    if (rect.bottom > window.innerHeight) {
        referenceElm.value.scrollIntoView(false);
    } else if (rect.top < 0) {
        referenceElm.value.scrollIntoView();
    }

    nextTick(() => select?.updatePopper?.());
}

function updateMinWidthFromTrigger() {
    nextTick(() => {
        const el = select?.triggerRef?.value?.$el;
        if (el?.offsetWidth) {
            minWidth.value = `${el.offsetWidth}px`;
        }
        windowWidth.value = `${window.innerWidth}px`;
    });
}

function handleResize() {
    updateMinWidthFromTrigger();
}

watch(() => props.visible, (val) => {
    if (val) {
        updateMinWidthFromTrigger();
        if (search.value && props.filterable) search.value.focus();
    } else {
        searchValue.value = '';
        select?.destroyPopper?.();
        emit('onHidePopper');
    }
});

onMounted(() => {
    window.addEventListener('resize', updateMinWidthFromTrigger);
    updateMinWidthFromTrigger();

    nextTick(() => {
        if (select?.triggerRef?.offsetWidth) {
            minWidth.value = select.triggerRef.offsetWidth + 'px';
        }

        if (props.visible && search.value && props.filterable) {
            search.value.focus();
        }
    });

    select.selectDropdownMounted.value = true;

    select?.on?.('updatePopper', () => {
        if (select?.visible) {
            select?.updatePopper?.();
        }
    });
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
    emit('onHidePopper');
});

defineExpose({
    search,
    wrap,
});
</script>

<style scoped lang="postcss">
.el-select-dropdown {
    will-change: transform, opacity;
    &-search {
        &-form {
            :deep(.el-input__editor) {
                height: 100%;
            }
        }
    }
}
</style>
